import { useEffect, Fragment } from "react";
import Link from "next/link";

import paths from "config/paths";

import Tag from "ui-components/Tag";
import Skeleton from "ui-components/Skeleton";

import { useFetchDataFileMacroListing } from "modules/macros/api";
import Button from "ui-components/Button";

const CreateUsingMacro = () => {
  const [
    macros,
    status,
    hasNextPage,
    getNextPage,
  ] = useFetchDataFileMacroListing();

  useEffect(() => {
    hasNextPage && getNextPage();
  }, [getNextPage, hasNextPage, macros]);

  const listing = macros.flat();
  const empty = status !== "loading" && listing.length === 0;

  return (
    <div className="max-w-screen-xl mx-auto px-4 sm:px-6">
      <div className="pb-4 mb-8">
        <h1 className="h2 text-center">Your Data File Macros</h1>
      </div>
      {empty && (
        <div className="text-center">
          <p>You dont have any macros yet!!!</p>
        </div>
      )}
      {!empty && listing.length === 0 && <Skeleton />}
      {listing.length >= 1 && (
        <table className="table table-auto w-full">
          <thead>
            <tr>
              <th className="font-semibold text-left table-cell w-1/5">
                <p className="px-1 mx-1">Macro Name</p>
              </th>
              <th className="font-semibold text-left table-cell w-2/5">
                <p className="px-1 mx-1">Input Required</p>
              </th>
              <th className="font-semibold text-left table-cell w-2/5">
                <p className="px-1 mx-1">Reference Output File</p>
              </th>
            </tr>
          </thead>
          <tbody>
            {listing.map((m) => (
              <tr key={m.id} className="border-t">
                <td>
                  <div className="p-1 m-1">
                    <p className="font-bold">{m.name}</p>
                    <p>
                      Created at{" "}
                      {new Date(m.created_time).toLocaleDateString(undefined, {
                        hour: "numeric",
                        minute: "numeric",
                        hour12: true,
                      })}
                    </p>
                    <p>{m.steps.length} Step Macro</p>
                    {[...m.steps].reverse().map(({ transform }, ind) => (
                      <div key={ind}>
                        {transform.length === 1 && (
                          <p>
                            {ind + 1}. {transform[0].type} on a file like{" "}
                            {transform[0].files.map((f) => (
                              <Link href={`${paths.dataFiles}/${f}`}>
                                <span key={f} className="text-primary-main">
                                  {f}
                                </span>
                              </Link>
                            ))}
                            .
                          </p>
                        )}
                        {transform.length >= 2 &&
                          transform.map((t, i) => (
                            <p key={i}>
                              {ind + 1}.{i + 1}. {t.type} on a file like{" "}
                              {t.files.map((f) => (
                                <Link href={`${paths.dataFiles}/${f}`}>
                                  <span key={f} className="text-primary-main">
                                    {f}
                                  </span>
                                </Link>
                              ))}
                              .
                            </p>
                          ))}
                      </div>
                    ))}
                    <Link href={`/transform-data/macro/${m.id}`}>
                      <Button>Use Macro</Button>
                    </Link>
                  </div>
                </td>
                <td>
                  <div className="p-1 m-1">
                    <p>
                      This macro needs {m.reference_input_files.length} input
                      file
                      {m.reference_input_files.length > 1 ? "s" : ""}.
                    </p>
                    {m.reference_input_files.map((f, ind) => (
                      <Fragment key={f}>
                        <p>
                          {m.reference_input_files.length === 1
                            ? "The File"
                            : `File ${ind + 1}`}{" "}
                          MUST have the following {m.input_columns[f].length}{" "}
                          columns:
                        </p>
                        <div className="flex flex-wrap">
                          {m.input_columns[f]
                            .sort((a, b) =>
                              a.toLowerCase().localeCompare(b.toLowerCase())
                            )
                            .map((c) => (
                              <Tag key={c}>{c}</Tag>
                            ))}
                        </div>
                      </Fragment>
                    ))}
                  </div>
                </td>
                <td>
                  <div className="p-1 m-1">
                    <p>
                      The output will look like{" "}
                      <Link
                        href={`${paths.dataFiles}/${m.reference_data_file}`}
                      >
                        <span className="text-primary-main">
                          {m.reference_data_file}
                        </span>
                      </Link>
                      , and have these {m.output_columns.length} columns:
                    </p>
                    <div className="flex flex-wrap">
                      {m.output_columns
                        .sort((a, b) =>
                          a.toLowerCase().localeCompare(b.toLowerCase())
                        )
                        .map((c) => (
                          <Tag key={c}>{c}</Tag>
                        ))}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default CreateUsingMacro;
